import * as React from 'react';
import styles from './index.less';
import type { RcFile } from 'antd/es/upload';
import { Upload } from 'antd';
import FCropperModal, { type FUploadNodeCoverImageType } from './FCropperModal';
import {
  DEFAULT_ACCEPT_IMAGE_TYPES,
  getFormatErrorMessage,
  isAcceptedImageFile,
} from '@/components/FUploadCover';
import { FServiceAPI, FI18n, FUtil } from '@freelog/tools-lib';
import fMessage from '@/components/fMessage';
import * as AHooks from 'ahooks';

export type { FUploadNodeCoverImageType };

const ACCEPT_IMAGE_TYPE_CONFIG: Record<
  FUploadNodeCoverImageType,
  { mime: string; ext: string }
> = {
  jpeg: { mime: 'image/jpeg', ext: '.jpg,.jpeg,.jpe' },
  png: { mime: 'image/png', ext: '.png' },
  gif: { mime: 'image/gif', ext: '.gif' },
};

function buildAcceptImageTypes(acceptImageTypes: FUploadNodeCoverImageType[]) {
  return acceptImageTypes.map((type) => ACCEPT_IMAGE_TYPE_CONFIG[type].ext).join(',');
}

function isImageFormatErrorMsg(msg: string) {
  return msg.includes('图片格式');
}

interface FUploadNodeCoverProps {
  children: React.ReactNode;
  /** 允许的图片类型，默认 jpg/png/gif */
  acceptImageTypes?: FUploadNodeCoverImageType[];

  onUploadSuccess?(url: string): void;

  onError?(error: string): void;
}

interface FUploadAvatarStates {
  naturalFile: File | null;
  image: string;
  uploading: boolean;
}

const initStates: FUploadAvatarStates = {
  naturalFile: null,
  image: '',
  uploading: false,
};

function FUploadNodeCover($prop: FUploadNodeCoverProps) {
  const acceptImageTypes = $prop.acceptImageTypes ?? DEFAULT_ACCEPT_IMAGE_TYPES;
  const formatErrorMessage = getFormatErrorMessage(acceptImageTypes);
  const ref = React.useRef<any>(null);
  const [$state, $setState] = AHooks.useSetState<FUploadAvatarStates>(initStates);

  const notifyError = React.useCallback(
    (error: string) => {
      $prop.onError?.(error);
    },
    [$prop.onError],
  );

  const beforeUpload = React.useCallback(
    (file: RcFile) => {
      if (!isAcceptedImageFile(file, acceptImageTypes)) {
        notifyError(formatErrorMessage);
        return false;
      }

      if (file.size > 5 * 1024 * 1024) {
        notifyError(
          // 图片大小超过限制
          FI18n.i18nNext.t('limit_resource_image_size') as string,
        );
        return false;
      }

      $setState({
        naturalFile: file,
      });

      const reader = new FileReader();
      reader.onload = () => {
        $setState({
          image: reader.result as string,
        });
      };
      reader.readAsDataURL(file);
      return false;
    },
    [acceptImageTypes, formatErrorMessage, notifyError, $setState],
  );

  async function upload(blob: Blob | null) {
    if (!blob) {
      fMessage('文件为空', 'error');
      return;
    }
    $setState({
      uploading: true,
    });
    const myFile = new File([blob], 'image.jpeg', {
      type: blob.type,
    });
    const { ret, errCode, msg, data: data_uploadImage }: {
      ret: number;
      errCode: number;
      msg: string;
      data: {
        url: string;
      };
    } = await FServiceAPI.Storage.uploadImage({
      file: myFile,
    });
    await FUtil.Tool.promiseSleep(1000);
    $setState({
      uploading: initStates['uploading'],
    });
    if (ret !== 0 || errCode !== 0) {
      const errorMsg = isImageFormatErrorMsg(msg) ? formatErrorMessage : msg;
      notifyError(errorMsg);
      return;
    }

    $prop.onUploadSuccess && $prop.onUploadSuccess(data_uploadImage.url);
    $setState(initStates);
  }

  return (
    <div className={styles.styles}>
      <Upload
        key={acceptImageTypes.join(',')}
        accept={buildAcceptImageTypes(acceptImageTypes)}
        beforeUpload={beforeUpload}
        multiple={false}
        showUploadList={false}
      >
        <div ref={ref} className={styles.uploadContainer}>
          {$prop.children}
        </div>
      </Upload>
      <FCropperModal
        acceptImageTypes={acceptImageTypes}
        uploading={$state.uploading}
        uploadRef={ref}
        imgSrc={$state.image}
        onOk={(blob) => {
          upload(blob);
        }}
        onCancel={() => {
          $setState({
            image: initStates['image'],
            naturalFile: initStates['naturalFile'],
          });
        }}
      />
    </div>
  );
}

export default FUploadNodeCover;
